'use client'

import { useEffect, useState } from 'react'
import throttle from 'lodash/fp/throttle'
import { NavBar, NavItem } from '@shc/ui'
import type { MenuItemData } from '@lib/types'
import useAnalytics from '@hooks/use-analytics'
import { doScroll } from '@hooks/useCustomScroll'

export type ProviderNavBarProps = {
  menuItems: MenuItemData[]
}

const ProviderNavBar = ({ menuItems }: ProviderNavBarProps) => {
  const { track: trackSnowplow } = useAnalytics()
  const [activeSection, setActiveSection] = useState('');
  
  const sectionElementIds = menuItems.map((link) => {
      const href = link.route
      const sectionIndex = href.indexOf('#')
      if (sectionIndex != -1) {
        return href.substring(sectionIndex + 1)
      }
      return null
    })
    .filter((ref) => ref !== null)

  const throttleMs = 100
  const handleScroll = throttle(throttleMs, () => {
    const position = window.scrollY
    for (const sectionElementId of sectionElementIds) {
      if (sectionElementId !== null) {
        const ele = document.getElementById(sectionElementId)
        if (
          ele !== null &&
          position >= ele.getBoundingClientRect().top + window.scrollY - 128
        ) {
          setActiveSection(sectionElementId)
        }
      }
    }
  })
  
  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  return (
    <NavBar
      id="sub-nav"
      alignment="start"
      sticky>
      {menuItems.map(({ name, route }) => {
        const active = activeSection === route.split('#')[1]

        return (
          <NavItem
            key={name}
            active={active}
            href={route}
            onClick={(e) => {
                trackSnowplow({
                  event: {
                    name: 'navigation_click',
                    data: {
                      navigation_tree: name,
                      navigation_level: 1,
                      navigation_subject: name,
                      navigation_url: route,
                    },
                  },
                  contexts: [{ name: 'section', data: { section_name: 'subnav' } }],
                })
                const targetAnchor = route.split('#')[1]
                if(targetAnchor){
                  const element = document.getElementById(targetAnchor)
                  if (element !== null) {
                    let scrollOffSet = 64;
                    if (typeof window !== 'undefined') {
                      const navBars = document.querySelectorAll("[id='sub-nav']")
                      if (navBars.length) {
                        scrollOffSet += (navBars[0] as HTMLElement).offsetHeight
                      }
                    }
                    doScroll(
                      element.getBoundingClientRect().top + window.scrollY - scrollOffSet
                    )
                    setActiveSection(targetAnchor)
                  }

                  // Update url without reload
                  window.history.replaceState(null, '', String(e.currentTarget))
                  e.preventDefault()
                }
            }}>
            {name}
          </NavItem>
        )
      })}
    </NavBar>
  )
}

export default ProviderNavBar
